// React
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DeliveryType } from "../../../../enums/company";

// Components
import {
    Button,
    Collapse,
    Divider,
    FormControl,
    InputLabel,
    ListItem,
    MenuItem,
    Paper,
    Select,
    TextField,
} from "@material-ui/core";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
    ColumnDef,
    SortingState,
} from "@tanstack/react-table";

// Types
import { IAppState } from "../../../../types/stores";
import { FlowExportXandrDriverFields } from "../../../../types/stores/flowExportXandrDriverFields";
import { Flow, FlowRelation } from "../../../../types/stores/flowTypes";

// Selectors
import { getFlowExportXandrDriverFieldsByFlowItemId } from "../../../../reducers/flowExportXandrDriverFields";
import { getFlowRelationsForSelectedFlow } from "../../../../reducers/flowRelations";

// Actions
import { updateAttribute, getDefaultDate, syncScheduleEndDateWithExpiration } from "../../../../actions/flowActions";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";

// Utils
import MomentUtils from "@date-io/moment";
import { newFlowExportXandrDriverFields } from "../../../../actions/flowExportXandrDriverFieldActions";
import MaterialTableTextField from "../../../shared/MaterialTableTextField";
import { Autocomplete } from "@material-ui/lab";
import { XandrRateCategory } from "../../../../types/stores/xandrRateCategory";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { formatExpirationDate } from "../../../../helpers/parseDate";

type Props = {
    flowItemId: number;
    canEdit: boolean;
    tapadDeliveryTypeId: DeliveryType;
};

const ExpandLess = <i className="material-icons">expand_less</i>;
const ExpandMore = <i className="material-icons">expand_more</i>;

const XandrDriverFields: React.FC<Props> = ({ flowItemId, canEdit, tapadDeliveryTypeId }) => {
    const dispatch = useDispatch();
    // local state
    const [selectedCategory, setSelectedCategory] = useState<XandrRateCategory | null>(null);
    const [isOpenGlobalAssignment, setIsOpenGlobalAssignment] = useState(true);
    const [globalExpirationDate, setGlobalExpirationDate] = useState<string | null>(null);
    const [globalExpansionType, setGlobalExpansionType] = useState("HOUSEHOLD");
    const [sorting, setSorting] = useState<SortingState>([]);
    const [deliveryType, setDeliveryType] = useState(0);

    const tapadDeliveryTypes = useSelector((state: IAppState) => state.vars.tapadDeliveryTypes);

    // redux selectors
    const flowRelations: Array<FlowRelation> = useSelector((state: IAppState) =>
        getFlowRelationsForSelectedFlow(state)
    );
    const xandrDriverFields = useSelector(
        (state: IAppState) => getFlowExportXandrDriverFieldsByFlowItemId(state)[flowItemId]
    );
    const xandrRateCategories = useSelector((state: IAppState) => state.xandrRateCategories.categories);
    const flow: Flow = useSelector((state: IAppState) => state.flows.byId[state.selected.flow]);
    const flowItemsById = useSelector((state: IAppState) => state.flowItems.byId);
    const rateCategoriesLoading = useSelector((state: IAppState) => state.xandrRateCategories.isLoading);
    const hasScheduleTemplatesFeature = useSelector((state: IAppState) =>
        state.session.enabledFeatures.includes("schedule-templates")
    );
    const isReadOnly = !canEdit || Boolean(flow && flow.IsLocked);

    const parentRelations = flowRelations.filter(x => x.ChildFlowItemId == flowItemId && x.ParentFlowItemId != 0);

    useEffect(() => {
        const thisDeliveryType = tapadDeliveryTypes.find(x => x.TapadDeliveryTypeId == tapadDeliveryTypeId);

        if (
            xandrDriverFields &&
            deliveryType != undefined &&
            tapadDeliveryTypeId != undefined &&
            deliveryType != 0 &&
            thisDeliveryType?.DeliveryType != DeliveryType.ThirdPartyCustom
        ) {
            for (let i = 0; i < xandrDriverFields.length; i++) {
                xandrDriverFields[i].BillingCategoryId = 0;
            }
        }
        if (thisDeliveryType) {
            setDeliveryType(thisDeliveryType.DeliveryType);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tapadDeliveryTypeId, tapadDeliveryTypes]);

    useEffect(() => {
        if (!xandrDriverFields || xandrDriverFields.length < parentRelations.length) {
            parentRelations.forEach(relation => {
                let parentFlowItemId = relation.ParentFlowItemId;
                let parentNode = flowItemsById[parentFlowItemId];

                // If parent is MDEN, resolve its true parent
                if (parentNode && parentNode.FlowItemType === "multiexport") {
                    const multiExportParentRelations = flowRelations.filter(
                        x => x.ChildFlowItemId === parentFlowItemId
                    );
                    multiExportParentRelations.forEach(parentRelation => {
                        const multiParentId = parentRelation.ParentFlowItemId;
                        if (!xandrDriverFields || !xandrDriverFields.find(x => x.ParentFlowItemId === multiParentId)) {
                            dispatch(
                                newFlowExportXandrDriverFields(
                                    flowItemId,
                                    multiParentId,
                                    selectedCategory ? selectedCategory.data_category_id : 0
                                )
                            );
                        }
                    });
                }

                if (parentNode && parentNode.FlowItemType !== "multiexport") {
                    // Only dispatch if the driver field for this parentFlowItemId does not exist
                    if (!xandrDriverFields || !xandrDriverFields.find(x => x.ParentFlowItemId === parentFlowItemId)) {
                        dispatch(
                            newFlowExportXandrDriverFields(
                                flowItemId,
                                parentFlowItemId,
                                selectedCategory ? selectedCategory.data_category_id : 0
                            )
                        );
                    }
                }
            });
        }
    }, [dispatch, parentRelations, xandrDriverFields, flowItemsById, flowRelations, flowItemId, selectedCategory]);

    useEffect(() => {
        if (selectedCategory == null && xandrDriverFields && xandrDriverFields.length > 0) {
            const dataCategoryId = xandrDriverFields[0].BillingCategoryId;
            const category = xandrRateCategories.find(x => x.data_category_id == dataCategoryId);
            if (category) {
                setSelectedCategory(category);
            }
        }
    }, [xandrDriverFields, selectedCategory, xandrRateCategories]);

    const savedExpirationDate = hasScheduleTemplatesFeature
        ? (xandrDriverFields || [])
              .map(field => formatExpirationDate(field.ExpirationDate))
              .filter((expirationDate): expirationDate is string => expirationDate != null)
              .sort()
              .pop()
        : (xandrDriverFields || [])
              .map(field => (field.ExpirationDate != null ? new Date(field.ExpirationDate) : null))
              .filter(
                  (expirationDate): expirationDate is Date =>
                      expirationDate != null && !Number.isNaN(expirationDate.getTime())
              )
              .sort((left, right) => left.getTime() - right.getTime())
              .pop()
              ?.toISOString();

    useEffect(() => {
        setGlobalExpirationDate(savedExpirationDate || getDefaultDate());
    }, [flowItemId, savedExpirationDate]);

    const columns: Array<ColumnDef<FlowExportXandrDriverFields>> =
        deliveryType && DeliveryType.ThirdPartyCustom == deliveryType
            ? [
                  {
                      id: "audienceName",
                      header: "Audience Name",
                      accessorKey: "AudienceName",
                      cell: info => renderTextField(info.row.original, "AudienceName"),
                      enableSorting: true,
                  },
                  {
                      id: "description",
                      header: "Description",
                      accessorKey: "Description",
                      cell: info => renderTextField(info.row.original, "Description"),
                      enableSorting: true,
                  },
                  {
                      id: "expansionType",
                      header: "Expansion Type",
                      accessorKey: "ExpansionType",
                      cell: info => renderExpansionType(info.row.original),
                      enableSorting: true,
                  },
                  {
                      id: "expirationDate",
                      header: "Expiration Date",
                      accessorKey: "ExpirationDate",
                      cell: info => renderExpirationDate(info.row.original),
                      enableSorting: true,
                  },
                  {
                      id: "rateType",
                      header: "Rate Type",
                      cell: () => <div>CPM</div>,
                      enableSorting: false,
                  },
                  {
                      id: "xandrRateCategory",
                      header: "Xandr Rate Category",
                      accessorKey: "BillingCategoryId",
                      cell: info => renderXandrRateCategories(info.row.original),
                      enableSorting: true,
                  },
              ]
            : [
                  {
                      id: "audienceName",
                      header: "Audience Name",
                      accessorKey: "AudienceName",
                      cell: info => renderTextField(info.row.original, "AudienceName"),
                      enableSorting: true,
                  },
                  {
                      id: "description",
                      header: "Description",
                      accessorKey: "Description",
                      cell: info => renderTextField(info.row.original, "Description"),
                      enableSorting: true,
                  },
                  {
                      id: "expansionType",
                      header: "Expansion Type",
                      accessorKey: "ExpansionType",
                      cell: info => renderExpansionType(info.row.original),
                      enableSorting: true,
                  },
                  {
                      id: "expirationDate",
                      header: "Expiration Date",
                      accessorKey: "ExpirationDate",
                      cell: info => renderExpirationDate(info.row.original),
                      enableSorting: true,
                  },
                  {
                      id: "rateType",
                      header: "Rate Type",
                      cell: () => <div>CPM</div>,
                      enableSorting: false,
                  },
              ];

    const renderTextField = (row: FlowExportXandrDriverFields, property: string) => (
        <MaterialTableTextField
            id={`XanderDriverField_${row.XandrDriverFieldId}_${property}`}
            value={row[property]}
            onChange={newValue => {
                dispatch(
                    updateAttribute("flowExportXandrDriverFields", row.XandrDriverFieldId, property, newValue, true)
                );
            }}
            disabled={isReadOnly}
        />
    );

    const renderExpansionType = (row: FlowExportXandrDriverFields) => (
        <Select
            key={`XanderDriverField_${row.XandrDriverFieldId}_ExpansionType`}
            style={{ fontSize: "14px", width: "100%" }}
            value={row.ExpansionType}
            disabled={isReadOnly}
            onChange={e =>
                dispatch(
                    updateAttribute(
                        "flowExportXandrDriverFields",
                        row.XandrDriverFieldId,
                        "ExpansionType",
                        e.target.value,
                        true
                    )
                )
            }
        >
            <MenuItem key={2} value={"HOUSEHOLD"}>
                Household
            </MenuItem>
        </Select>
    );

    const renderXandrRateCategories = (row: FlowExportXandrDriverFields) => (
        <Select
            key={`XanderDriverField_${row.XandrDriverFieldId}_ExpansionType`}
            style={{ fontSize: "14px", width: "100%" }}
            value={row.BillingCategoryId}
            disabled={isReadOnly}
            onChange={e =>
                dispatch(
                    updateAttribute(
                        "flowExportXandrDriverFields",
                        row.XandrDriverFieldId,
                        "BillingCategoryId",
                        e.target.value,
                        true
                    )
                )
            }
        >
            {xandrRateCategories.map(x => (
                <MenuItem key={"xander-rate-category-" + x.data_category_id} value={x.data_category_id}>
                    {`${x.category_name} (${x.category_price})`}
                </MenuItem>
            ))}
        </Select>
    );

    const renderExpirationDate = (row: FlowExportXandrDriverFields) => (
        <MuiPickersUtilsProvider utils={MomentUtils}>
            <DateTimePicker
                key={`XanderDriverField_${row.XandrDriverFieldId}_ExpirationDate`}
                style={{ fontSize: "14px", width: "100%" }}
                format={"MMM DD, YYYY HH:mm"}
                value={hasScheduleTemplatesFeature ? formatExpirationDate(row.ExpirationDate) : row.ExpirationDate}
                onChange={e => {
                    const newDateTime = hasScheduleTemplatesFeature ? formatExpirationDate(e) : e;
                    dispatch(
                        updateAttribute(
                            "flowExportXandrDriverFields",
                            row.XandrDriverFieldId,
                            "ExpirationDate",
                            newDateTime,
                            true
                        )
                    );
                    dispatch(syncScheduleEndDateWithExpiration(newDateTime));
                }}
                disabled={isReadOnly}
            />
        </MuiPickersUtilsProvider>
    );

    const handleGlobalDateTimeChange = (globalDate: MaterialUiPickersDate) => {
        const newDateTime = hasScheduleTemplatesFeature
            ? formatExpirationDate(globalDate)
            : globalDate
              ? globalDate.utc().format()
              : null;
        setGlobalExpirationDate(newDateTime);
    };

    const assignGlobalValues = () => {
        if (isReadOnly) {
            return;
        }

        for (const field of xandrDriverFields) {
            dispatch(
                updateAttribute(
                    "flowExportXandrDriverFields",
                    field.XandrDriverFieldId,
                    "ExpansionType",
                    globalExpansionType
                )
            );

            dispatch(
                updateAttribute(
                    "flowExportXandrDriverFields",
                    field.XandrDriverFieldId,
                    "ExpirationDate",
                    globalExpirationDate
                )
            );

            dispatch(
                updateAttribute(
                    "flowExportXandrDriverFields",
                    field.XandrDriverFieldId,
                    "BillingCategoryId",
                    selectedCategory?.data_category_id || 0
                )
            );
        }

        dispatch(syncScheduleEndDateWithExpiration(globalExpirationDate));
    };

    const table = useReactTable({
        data: xandrDriverFields || [],
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId: row => String(row.XandrDriverFieldId),
    });

    return (
        <>
            <div className="flow-edit-header">
                <div style={{ flexGrow: 1, position: "relative" }}>
                    <p>Xandr Driver File Fields</p>
                </div>
            </div>
            <Divider style={{ margin: "20px auto" }} />
            <ListItem
                key="button-global-fields-assignment"
                style={{ display: "flex", justifyContent: "center" }}
                button
                disabled={isReadOnly}
                onClick={() => setIsOpenGlobalAssignment(!isOpenGlobalAssignment)}
            >
                <span style={{ display: "flex", fontSize: "16px" }}>Global Fields Assignment</span>
                <span style={{ display: "flex", position: "absolute", right: "0", fontSize: "16px" }}>
                    {isOpenGlobalAssignment ? ExpandLess : ExpandMore}
                </span>
            </ListItem>
            <Collapse key="container-global-fields-assignment" in={isOpenGlobalAssignment} timeout="auto" unmountOnExit>
                <div className="row">
                    <div className="col col-sm-4 col-md-3">
                        <FormControl fullWidth style={{ margin: "10px 0 0" }}>
                            <InputLabel shrink style={{ fontSize: 14 }}>
                                Expansion Type
                            </InputLabel>
                            <Select
                                key={`XanderDriverField_global_ExpansionType`}
                                style={{ fontSize: "14px", width: "100%" }}
                                value={"HOUSEHOLD"}
                                disabled={isReadOnly}
                                onChange={e => setGlobalExpansionType(e.target.value as string)}
                            >
                                <MenuItem key={2} value={"HOUSEHOLD"}>
                                    Household
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className="col col-sm-4 col-md-3">
                        {deliveryType && DeliveryType.ThirdPartyCustom == deliveryType ? (
                            <FormControl fullWidth style={{ margin: "10px 0 0" }}>
                                <Autocomplete
                                    id="combo-box-demo"
                                    options={xandrRateCategories}
                                    value={selectedCategory}
                                    disabled={isReadOnly}
                                    getOptionLabel={option => option.category_name}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="CPM ($)"
                                            helperText={
                                                !selectedCategory
                                                    ? "* Please contact your Experian admin to raise a CPM request if not available."
                                                    : ""
                                            }
                                        />
                                    )}
                                    onChange={(_e, value) => setSelectedCategory(value)}
                                    loading={rateCategoriesLoading}
                                    loadingText={"Loading Categories..."}
                                    fullWidth
                                />
                            </FormControl>
                        ) : null}
                    </div>
                </div>
                <div className="row">
                    <div className="col col-sm-4 col-md-3">
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                            <DateTimePicker
                                label="Expiration Date"
                                style={{ fontSize: "14px", width: "100%", margin: "10px 0 0" }}
                                format={"MMM DD, YYYY HH:mm"}
                                value={globalExpirationDate}
                                disabled={isReadOnly}
                                onChange={e => handleGlobalDateTimeChange(e)}
                            />
                        </MuiPickersUtilsProvider>
                    </div>
                    <div className="col col-sm-4 col-md-6" style={{ height: "58px" }}>
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={isReadOnly}
                            onClick={assignGlobalValues}
                            style={{ position: "absolute", bottom: "0" }}
                        >
                            Assign
                        </Button>
                    </div>
                </div>
            </Collapse>
            <Paper style={{ marginTop: "30px", width: "100%" }}>
                <div className="m-t-md">
                    <div style={{ maxHeight: 400, overflow: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "0px" }}>
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                style={{
                                                    backgroundColor: "#0075bc",
                                                    color: "#fff",
                                                    fontWeight: 400,
                                                    fontSize: "14px",
                                                    position: "sticky",
                                                    top: 0,
                                                    padding: "8px",
                                                    textAlign: "left",
                                                    zIndex: 10,
                                                    cursor: header.column.getCanSort() ? "pointer" : "default",
                                                }}
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                {header.isPlaceholder ? null : (
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {header.column.getIsSorted() && (
                                                            <i className="material-icons" style={{ fontSize: 16 }}>
                                                                {header.column.getIsSorted() === "asc"
                                                                    ? "arrow_upward"
                                                                    : "arrow_downward"}
                                                            </i>
                                                        )}
                                                    </div>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows.map(row => (
                                    <tr
                                        key={row.id}
                                        style={{
                                            fontSize: 12,
                                            padding: "2px",
                                            borderBottom: "1px solid rgba(224, 224, 224, 1)",
                                        }}
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id} style={{ padding: "16px" }}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Paper>
            <Divider style={{ margin: "40px auto 20px auto" }} />
        </>
    );
};

export default XandrDriverFields;
